/**
 * Registry of features that show a one-shot "Try" badge + stepped walkthrough
 * on Ask Puntt home. Consumers: Deep Artwork Review (`artwork_review`) and
 * the home Chat/Project composer (`home_project`).
 */

import howItWorksPreview from "./assets/deep-review-walkthrough-how-it-works.png";
import introPreview from "./assets/deep-review-walkthrough-intro.png";
import homeProjectHowItWorksPreview from "./assets/home-project-walkthrough-how-it-works.png";
import homeProjectIntroPreview from "./assets/home-project-walkthrough-intro.png";
import homeProjectTeamApprovePreview from "./assets/home-project-walkthrough-team-approve.png";

/** 16:9 frame matching the 1024×576 hero shots so every intro step is the same height. */
export const TRY_WALKTHROUGH_PREVIEW_FRAME_CLASS = "aspect-video w-full";

/** Keep compose/upload steps as tall as an intro with a 16:9 hero. */
export const TRY_WALKTHROUGH_DIALOG_MIN_H_CLASS = "min-h-[520px]";

export type TryStepKind = "intro" | "upload" | "compose";

export type TryIntroStep = {
  kind: "intro";
  /** i18n key under tryWalkthrough.<featureId>.steps.<stepKey> */
  stepKey: string;
  /** Full-bleed hero for the step. Alt copy is `steps.<stepKey>.previewAlt`. */
  previewSrc?: string;
};

export type TryUploadStep = {
  kind: "upload";
  stepKey: string;
};

export type TryComposeStep = {
  kind: "compose";
  stepKey: string;
};

export type TryStep = TryIntroStep | TryUploadStep | TryComposeStep;

export type TryFeatureConfig = {
  featureId: string;
  /**
   * Lookup key for `TRY_FEATURES_BY_CARD_ID`: a skill card id, or a home
   * chrome sentinel (`home_project`) that is not a WorkflowCards clickId.
   */
  cardId: string;
  launchReviewMode: "deep" | "standard";
  steps: readonly TryStep[];
};

export const ARTWORK_REVIEW_TRY_FEATURE: TryFeatureConfig = {
  featureId: "artwork_review",
  cardId: "artwork_review",
  launchReviewMode: "deep",
  steps: [
    {
      kind: "intro",
      stepKey: "intro",
      previewSrc: introPreview,
    },
    {
      kind: "intro",
      stepKey: "howItWorks",
      previewSrc: howItWorksPreview,
    },
    {
      kind: "upload",
      stepKey: "upload",
    },
  ],
};

export const HOME_PROJECT_TRY_FEATURE: TryFeatureConfig = {
  featureId: "home_project",
  cardId: "home_project",
  launchReviewMode: "standard",
  steps: [
    {
      kind: "intro",
      stepKey: "intro",
      previewSrc: homeProjectIntroPreview,
    },
    {
      kind: "intro",
      stepKey: "howItWorks",
      previewSrc: homeProjectHowItWorksPreview,
    },
    {
      kind: "intro",
      stepKey: "teamApprove",
      previewSrc: homeProjectTeamApprovePreview,
    },
    {
      kind: "compose",
      stepKey: "compose",
    },
  ],
};

export const TRY_FEATURES_BY_CARD_ID: Record<string, TryFeatureConfig> = {
  artwork_review: ARTWORK_REVIEW_TRY_FEATURE,
  home_project: HOME_PROJECT_TRY_FEATURE,
};

export function getTryFeatureForCard(
  cardId: string,
): TryFeatureConfig | undefined {
  return TRY_FEATURES_BY_CARD_ID[cardId];
}
